/**
 * Rate Limiter
 * Prevents DOS attacks via repeated script execution
 * Enforces maximum executions per control per time window
 */

export interface RateLimitConfig {
  maxExecutionsPerWindow: number; // e.g., 100 scripts per window
  windowDurationMs: number; // e.g., 60000 ms (1 minute)
  perControl?: boolean; // If true, limit per control ID; if false, limit globally
}

export interface RateLimitStatus {
  allowed: boolean;
  reason?: string;
  remainingExecutions: number;
  resetTimeMs: number; // Milliseconds until window resets
}

/**
 * Rate limiter for script execution
 * Uses sliding window algorithm to prevent DOS
 */
export class RateLimiter {
  private executionHistory: Map<string, number[]> = new Map(); // key -> [timestamps]
  private config: RateLimitConfig;

  constructor(config?: RateLimitConfig) {
    this.config = {
      maxExecutionsPerWindow: config?.maxExecutionsPerWindow ?? 1000,
      windowDurationMs: config?.windowDurationMs ?? 60000,
      perControl: config?.perControl ?? false,
    };
  }

  /**
   * Check if execution is allowed for the given control
   */
  checkLimit(controlId: string): RateLimitStatus {
    const key = this.config.perControl ? controlId : 'global';
    const now = Date.now();

    // Initialize tracking for this key if needed
    if (!this.executionHistory.has(key)) {
      this.executionHistory.set(key, []);
    }

    const timestamps = this.executionHistory.get(key)!;

    // Remove timestamps outside the current window
    const windowStart = now - this.config.windowDurationMs;
    const validTimestamps = timestamps.filter((t) => t > windowStart);
    this.executionHistory.set(key, validTimestamps);

    // Check if limit exceeded
    if (validTimestamps.length >= this.config.maxExecutionsPerWindow) {
      const oldestTimestamp = validTimestamps[0];
      const resetTimeMs = oldestTimestamp + this.config.windowDurationMs - now;

      return {
        allowed: false,
        reason: `Rate limit exceeded (${this.config.maxExecutionsPerWindow} per ${this.config.windowDurationMs}ms)`,
        remainingExecutions: 0,
        resetTimeMs: Math.max(0, resetTimeMs),
      };
    }

    // Record this execution
    validTimestamps.push(now);
    this.executionHistory.set(key, validTimestamps);

    const remainingExecutions =
      this.config.maxExecutionsPerWindow - validTimestamps.length;
    const oldestTimestamp = validTimestamps[0];
    const resetTimeMs = oldestTimestamp + this.config.windowDurationMs - now;

    return {
      allowed: true,
      remainingExecutions,
      resetTimeMs: Math.max(0, resetTimeMs),
    };
  }

  /**
   * Clear history for a specific control
   */
  clearHistory(controlId: string): void {
    const key = this.config.perControl ? controlId : 'global';
    this.executionHistory.delete(key);
  }

  /**
   * Clear all history
   */
  clearAllHistory(): void {
    this.executionHistory.clear();
  }

  /**
   * Get execution count for a control in the current window
   */
  getExecutionCount(controlId: string): number {
    const key = this.config.perControl ? controlId : 'global';
    const timestamps = this.executionHistory.get(key);

    if (!timestamps) return 0;

    const now = Date.now();
    const windowStart = now - this.config.windowDurationMs;
    return timestamps.filter((t) => t > windowStart).length;
  }

  /**
   * Update configuration at runtime
   */
  updateConfig(config: Partial<RateLimitConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
